import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import classNames from 'classnames';
import React, { isValidElement, useEffect } from 'react';

import buttonStyles from '../../Button/button.module.css';
import { Icon } from '../../Icon';
import carouselStyles from '../splide.module.css';

const getPerPageForBreakpoint = (
  currentPerPage: number,
  breakpoint: number,
) => {
  if (breakpoint === 1024) {
    return currentPerPage > 2 ? 3 : currentPerPage;
  }
  if (breakpoint === 768) {
    return currentPerPage > 2 ? 2 : currentPerPage;
  }
  return currentPerPage;
};

// Guards against a caller passing 0 or a negative perMove, which would
// otherwise divide-by-zero/underflow the pageCount and dot-click math below.
const clampPerMove = (value: number) => Math.max(1, value);

const getBaseGap = (gap: number | undefined, mobileGap: number | undefined) => {
  if (mobileGap !== undefined) return mobileGap * 4;
  if (gap !== undefined) return gap * 4;
  return 12;
};

type Props = {
  headline?: string | null | undefined;
  items: Array<React.ReactNode>;
  gap?: 3 | 5 | 6;
  // Overrides `gap` specifically below the 768px breakpoint, for callers
  // that want a smaller gap on mobile than on desktop. Ignored above 768px.
  mobileGap?: 3 | 5 | 6;
  perPage?: number;
  // Fixed number of slides advanced per navigation, regardless of perPage.
  // Below the 768px breakpoint this defaults to 1 (unchanged); at 768px
  // and up it defaults to matching that breakpoint's resolved perPage
  // (a full "page" per click), same as before this prop existed.
  perMove?: number;
  arrowPosition?: 'top' | 'middle';
  onSlide?: (index: number) => void;
  pagination?: boolean;
  // Jumps back to the first page after the last (and back to the last from
  // the first) instead of stopping at the ends, via Splide's `rewind`. This
  // is not an infinite/clone-based loop — it's a single jump at the edges.
  loop?: boolean;
  resetTrigger?: number;
};

export const Carousel: React.FC<Props> = ({
  arrowPosition = 'top',
  // No default here (unlike the other props): callers that don't pass
  // `gap` keep the exact pre-existing spacing (12px below 768px, 20px at
  // and above it) untouched. Only callers that explicitly opt in get a
  // gap consistent across every breakpoint — see the base/breakpoint
  // options below.
  gap,
  headline,
  items,
  loop = false,
  mobileGap,
  onSlide = () => {},
  pagination = false,
  perMove,
  perPage = 4,
  resetTrigger,
}) => {
  const mainRef = React.useRef<Splide>(null);
  // Splide ties its own `pagination` option to reachable focus positions
  // rather than pages of `perPage` items whenever `focus` is set (which we
  // always do), so with a custom `perMove` it ends up with one dot per
  // slide instead of one per page. Track page count/active page ourselves,
  // as the number of distinct sliding windows perMove steps through
  // (not items/perPage, which only holds when perMove equals perPage).
  // Initialized to match the base (pre-768px) Splide options below, not
  // the raw props, so pageCount/dots are correct on the very first render
  // at that breakpoint instead of jumping once onMounted resolves the
  // real values. On wider viewports onMounted/onResized still update
  // these to the breakpoint-resolved perPage/perMove, same as before.
  const [resolvedPerPage, setResolvedPerPage] = React.useState(() =>
    getPerPageForBreakpoint(perPage, 768),
  );
  const [resolvedPerMove, setResolvedPerMove] = React.useState(() =>
    clampPerMove(perMove ?? 1),
  );
  const [activePage, setActivePage] = React.useState(0);

  useEffect(() => {
    const splideElement = mainRef.current;

    if (splideElement) {
      splideElement.go(0);
    }
  }, [resetTrigger]);

  if (items.length === 0) return null;

  // The number of distinct sliding windows perMove steps through (not
  // items/perPage, which only holds when perMove equals perPage). This is
  // the same whether or not `loop` is enabled — looping only changes what
  // happens after the last window (wraps back to the first via Splide's
  // `rewind` instead of stopping), not how many windows there are.
  const computePageCount = (currentPerPage: number, currentPerMove: number) => {
    if (items.length <= currentPerPage) return 1;
    return Math.ceil((items.length - currentPerPage) / currentPerMove) + 1;
  };

  // Splide clamps the reachable index to maxStart when it isn't a
  // multiple of perMove, which would otherwise map to the previous page
  // via plain floor division — clamp to the last page explicitly to match.
  const computeActivePage = (
    rawIndex: number,
    currentPerPage: number,
    currentPerMove: number,
  ) => {
    const maxStart = Math.max(0, items.length - currentPerPage);
    if (rawIndex >= maxStart) {
      return computePageCount(currentPerPage, currentPerMove) - 1;
    }
    return Math.floor(rawIndex / currentPerMove);
  };

  const pageCount = computePageCount(resolvedPerPage, resolvedPerMove);
  const maxStartIndex = Math.max(0, items.length - resolvedPerPage);

  const goToPage = (page: number) =>
    mainRef.current?.go(Math.min(page * resolvedPerMove, maxStartIndex));

  // Splide's own rewind control (Controller.go('>'/'<')) steps its internal
  // index by perMove regardless of our page windows, so with perPage>1 it
  // takes extra clicks past the last window before it actually wraps.
  // Driving prev/next through our own page math (same as the dots) instead
  // wraps exactly on the click that reaches the last/first page.
  const isFirstPage = activePage === 0;
  const isLastPage = activePage === pageCount - 1;

  const goToPrevPage = () => {
    if (isFirstPage) {
      if (loop) goToPage(pageCount - 1);
      return;
    }
    goToPage(activePage - 1);
  };

  const goToNextPage = () => {
    if (isLastPage) {
      if (loop) goToPage(0);
      return;
    }
    goToPage(activePage + 1);
  };

  const paginationArrows = (
    <>
      <button
        aria-label="Previous slide"
        className={classNames(
          'z-50 !rounded-full p-2',
          buttonStyles.UiButton__variant__tertiary,
        )}
        disabled={!loop && isFirstPage}
        onClick={goToPrevPage}
        type="button"
      >
        <div className="flex flex-row justify-center">
          <Icon name="ic_arrow_left" />
        </div>
      </button>
      <button
        aria-label="Next slide"
        className={classNames(
          'z-50 !rounded-full p-2',
          buttonStyles.UiButton__variant__tertiary,
        )}
        disabled={!loop && isLastPage}
        onClick={goToNextPage}
        type="button"
      >
        <div className="flex flex-row justify-center">
          <Icon name="ic_arrow_right" />
        </div>
      </button>
    </>
  );

  return (
    <div className={classNames(carouselStyles.UiCarousel)}>
      <Splide
        ref={mainRef}
        hasTrack={false}
        onMounted={(splide) => {
          const currentPerPage = splide.options.perPage ?? perPage;
          const currentPerMove = splide.options.perMove ?? currentPerPage;
          setResolvedPerPage(currentPerPage);
          setResolvedPerMove(currentPerMove);
        }}
        onMoved={(splide, newIndex) => {
          onSlide(newIndex);
          const currentPerPage = splide.options.perPage ?? perPage;
          const currentPerMove = splide.options.perMove ?? currentPerPage;
          setActivePage(
            computeActivePage(newIndex, currentPerPage, currentPerMove),
          );
        }}
        onResized={(splide) => {
          const currentPerPage = splide.options.perPage ?? perPage;
          const currentPerMove = splide.options.perMove ?? currentPerPage;
          setResolvedPerPage(currentPerPage);
          setResolvedPerMove(currentPerMove);
          setActivePage(
            computeActivePage(splide.index, currentPerPage, currentPerMove),
          );
        }}
        options={{
          label: 'carousel',
          // `rewind` (jump back to the first window after the last, rather
          // than Splide's `type: 'loop'`, which clones slides to cycle
          // through every individual item forever) keeps navigation aligned
          // with our own perPage-based window count instead of introducing
          // extra reachable positions pagination doesn't account for.
          rewind: loop,
          pagination: false,
          mediaQuery: 'min',

          arrows: false,
          focus: 0,
          gap: getBaseGap(gap, mobileGap),
          perMove: clampPerMove(perMove ?? 1),
          perPage: getPerPageForBreakpoint(perPage, 768),

          flickPower: 300,
          flickMaxPages: 0.3,

          breakpoints: {
            768: {
              gap: (gap ?? 5) * 4,
              perMove: clampPerMove(
                perMove ?? getPerPageForBreakpoint(perPage, 768),
              ),
              perPage: getPerPageForBreakpoint(perPage, 768),
            },
            1024: {
              gap: (gap ?? 5) * 4,
              perMove: clampPerMove(
                perMove ?? getPerPageForBreakpoint(perPage, 1024),
              ),
              perPage: getPerPageForBreakpoint(perPage, 1024),
            },
            1280: {
              gap: (gap ?? 5) * 4,
              perMove: clampPerMove(
                perMove ?? getPerPageForBreakpoint(perPage, 1280),
              ),
              perPage: getPerPageForBreakpoint(perPage, 1280),
            },
          },
        }}
      >
        {headline && (
          <div className="mb-4 flex w-full justify-between space-x-3">
            <h2 className="relative z-20 text-balance text-md text-neutral-4 md:text-lg">
              {headline}
            </h2>

            {arrowPosition === 'top' && pageCount > 1 && (
              <div className={carouselStyles.UiCarousel__arrows__top}>
                {paginationArrows}
              </div>
            )}
          </div>
        )}

        <div className="relative">
          {arrowPosition === 'middle' && pageCount > 1 && (
            <div className={carouselStyles.UiCarousel__arrows__middle}>
              {paginationArrows}
            </div>
          )}

          <SplideTrack className={classNames(carouselStyles.UiCarousel__track)}>
            {items.map((item, index) => (
              <SplideSlide
                key={
                  isValidElement(item) && item.key !== null ? item.key : index
                }
                className={classNames(carouselStyles.UiCarousel__track__slide)}
              >
                <div className="w-full">{item}</div>
              </SplideSlide>
            ))}
          </SplideTrack>
        </div>

        {pagination && pageCount > 1 && (
          <div className={carouselStyles.UiCarousel__pagination}>
            {Array.from({ length: pageCount }, (_, page) => (
              <button
                key={page}
                aria-current={page === activePage ? 'page' : undefined}
                aria-label={`Go to page ${page + 1}`}
                className={classNames(
                  carouselStyles.UiCarousel__pagination__dot,
                  {
                    [carouselStyles.UiCarousel__pagination__dot__active]:
                      page === activePage,
                  },
                )}
                onClick={() =>
                  mainRef.current?.go(
                    Math.min(page * resolvedPerMove, maxStartIndex),
                  )
                }
                type="button"
              />
            ))}
          </div>
        )}
      </Splide>
    </div>
  );
};
